import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/Navbar";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { api } from "@/api";

const NotificationBell = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const ref = useRef(null);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => {
      clearInterval(iv);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 border border-[#E5E2D9] flex items-center justify-center text-[#1A1A1A] hover:border-[#D4AF37] transition-colors duration-200"
        data-testid="notification-bell-button"
        aria-label={t("notif_bell")}
      >
        <Bell size={17} />
        {data.unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-[#D4AF37] text-[#1A1A1A] text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="notification-badge">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white border border-[#E5E2D9] shadow-xl z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E2D9]">
            <span className="font-display font-bold text-[#1A1A1A]">{t("notif_bell")}</span>
            {data.unread > 0 && (
              <button onClick={markAll} className="inline-flex items-center gap-1 text-xs font-bold text-[#B5952F] hover:underline" data-testid="notification-mark-read">
                <CheckCheck size={14} /> {t("notif_mark_read")}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-[#E5E2D9]">
            {data.notifications.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-[#5C5C5C]" data-testid="notification-empty">{t("notif_empty")}</div>
            )}
            {data.notifications.map((n) => (
              <div key={n.id} className={`px-5 py-4 ${n.read ? "" : "bg-[#FDFCF8]"}`} data-testid={`notification-item-${n.id}`}>
                <div className="flex items-start gap-3">
                  {!n.read && <span className="mt-1.5 w-2 h-2 shrink-0 bg-[#D4AF37] rounded-full" />}
                  <div className={n.read ? "pl-5" : ""}>
                    <div className="text-sm font-bold text-[#1A1A1A]">{n.title}</div>
                    <div className="text-xs text-[#5C5C5C] mt-0.5 leading-relaxed">{n.body}</div>
                    <div className="text-[10px] text-[#B5952F] font-bold uppercase tracking-wider mt-1.5 tabular">
                      {(n.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const DashShell = ({ title, subtitle, children, testid }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const doLogout = async () => {
    setBusy(true);
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid={testid}>
      <header className="bg-white border-b border-[#E5E2D9] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Logo size={36} />
          <div className="flex items-center gap-4">
            <LangToggle />
            <NotificationBell />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-bold text-[#1A1A1A]" data-testid="dashboard-user-name">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#B5952F] font-bold">{user?.role}</div>
            </div>
            <button
              onClick={doLogout}
              disabled={busy}
              className="border border-[#1A1A1A] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-200"
              data-testid="logout-button"
            >
              {t("nav_logout")}
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-10">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">{title}</h1>
          {subtitle && <p className="text-sm text-[#5C5C5C] mt-2">{subtitle}</p>}
        </div>
        {children}
      </main>
    </div>
  );
};

export const Kpi = ({ label, value, gold, testid }) => (
  <div className="bg-white border border-[#E5E2D9] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={testid}>
    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C5C5C]">{label}</div>
    <div className={`font-display text-2xl sm:text-3xl font-bold tabular mt-2 ${gold ? "text-[#B5952F]" : "text-[#1A1A1A]"}`}>{value}</div>
  </div>
);

export const StatusPill = ({ status }) => {
  const styles = {
    pending: "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/40",
    approved: "bg-[#089950]/10 text-[#089950] border-[#089950]/40",
    active: "bg-[#089950]/10 text-[#089950] border-[#089950]/40",
    rejected: "bg-[#E03131]/10 text-[#E03131] border-[#E03131]/40",
    completed: "bg-[#1A1A1A]/5 text-[#1A1A1A] border-[#1A1A1A]/20",
    paid: "bg-[#089950]/10 text-[#089950] border-[#089950]/40",
    accrued: "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/40",
  };
  return (
    <span className={`inline-block border text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 ${styles[status] || styles.pending}`} data-testid={`status-${status}`}>
      {status}
    </span>
  );
};
